/**
 * Stock screener service — API-backed shim.
 *
 * Previously this module shipped a hardcoded 120-company `COMPREHENSIVE_INDIAN_STOCKS`
 * array, which meant the UI returned stale data even when the ingestion pipeline was
 * fully populated. It now proxies to the live backend at `/api/india/*` and preserves
 * the same public function signatures so existing callers (CompanyResearchPage,
 * NewsPage, AddHoldingPage) continue to work without edits.
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/india`;

// Retained for dropdowns. These are display labels only — filtering is done server-side
// against the canonical taxonomy in `india_securities_master`.
export const INDIAN_SECTORS = [
  'All Sectors',
  'Automobiles',
  'Banking',
  'Cement',
  'Chemicals',
  'Consumer Goods',
  'Energy',
  'Financial Services',
  'Healthcare',
  'IT Services',
  'Manufacturing',
  'Media & Entertainment',
  'Metals',
  'Oil & Gas',
  'Pharmaceuticals',
  'Real Estate',
  'Retail',
  'Telecommunications',
  'Textiles',
  'Transportation',
];

// Market cap categories (in INR crores).
export const MARKET_CAP_RANGES = {
  'Large Cap': { min: 20000, max: Infinity },
  'Mid Cap':   { min: 5000,  max: 20000 },
  'Small Cap': { min: 500,   max: 5000 },
  'Micro Cap': { min: 0,     max: 500 },
};

/**
 * Normalize a backend security row to the shape the legacy UI expects.
 * Accepts either the `/api/india/securities` list shape or `/api/india/{symbol}` detail shape.
 */
function normalize(row) {
  if (!row) return null;
  const symbolRaw = row.symbol || row.nse_symbol || row.ticker || '';
  const exchange = (row.exchange || (symbolRaw.endsWith('.BO') ? 'BSE' : 'NSE')).toUpperCase();
  const symbolSuffix = symbolRaw.includes('.') ? symbolRaw : `${symbolRaw}.${exchange === 'BSE' ? 'BO' : 'NS'}`;
  return {
    id: row.isin || row.id || symbolSuffix,
    isin: row.isin || null,
    name: row.company_name || row.name || symbolRaw,
    symbol: symbolSuffix,
    exchange,
    isListed: row.status !== 'delisted',
    sector: row.sector || row.macro_sector || 'Others',
    industry: row.industry || row.basic_industry || '',
    marketCap: row.market_cap_cr ?? row.market_cap ?? null,
    pe: row.pe ?? null,
    pb: row.pb ?? null,
    dividend: row.dividend_yield ?? null,
    eps: row.eps ?? null,
    bookValue: row.book_value ?? null,
    debtToEquity: row.debt_to_equity ?? null,
    roe: row.roe ?? null,
    currentPrice: row.current_price ?? row.last_price ?? null,
    priceChange: row.price_change ?? null,
    priceChangePercent: row.price_change_pct ?? null,
    description: row.description || '',
    website: row.website || '',
    ceo: row.ceo || '',
    foundedYear: row.incorporation_year || null,
    employees: row.employees || null,
  };
}

/**
 * Search / screen companies. Returns the same shape as the legacy array-based implementation.
 * Live data comes from the securities master + market snapshots; filters are applied server-side.
 */
export const searchCompanies = async (filters = {}) => {
  try {
    const params = {};
    if (filters.sector && filters.sector !== 'All Sectors') params.sector = filters.sector;
    if (filters.marketCapCategory) {
      const range = MARKET_CAP_RANGES[filters.marketCapCategory];
      if (range) {
        params.market_cap_min = range.min;
        if (Number.isFinite(range.max)) params.market_cap_max = range.max;
      }
    }
    if (filters.marketCapMin !== undefined) params.market_cap_min = filters.marketCapMin;
    if (filters.marketCapMax !== undefined) params.market_cap_max = filters.marketCapMax;
    if (filters.peMin !== undefined) params.pe_min = filters.peMin;
    if (filters.peMax !== undefined) params.pe_max = filters.peMax;
    if (filters.pbMin !== undefined) params.pb_min = filters.pbMin;
    if (filters.pbMax !== undefined) params.pb_max = filters.pbMax;
    if (filters.searchQuery) params.q = filters.searchQuery;
    params.limit = filters.limit || 200;

    const { data } = await axios.get(`${API}/securities`, { params });
    const rows = Array.isArray(data) ? data : data?.items || data?.results || [];
    return rows.map(normalize).filter(Boolean);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[stockScreenerService] searchCompanies failed:', err?.message);
    }
    return [];
  }
};

/**
 * Fetch detail for a single security. `symbol` is accepted in either `.NS`/`.BO` form or bare.
 */
export const getCompanyDetails = async (symbol) => {
  if (!symbol) return null;
  const bare = symbol.replace(/\.(NS|BO)$/i, '');
  try {
    const { data } = await axios.get(`${API}/${encodeURIComponent(bare)}`);
    return normalize(data?.company || data);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[stockScreenerService] getCompanyDetails failed for', symbol, ':', err?.message);
    }
    return null;
  }
};

export const getMarketCapCategory = (marketCap) => {
  if (marketCap == null) return null;
  if (marketCap >= 20000) return 'Large Cap';
  if (marketCap >= 5000) return 'Mid Cap';
  if (marketCap >= 500) return 'Small Cap';
  return 'Micro Cap';
};

export const formatMarketCap = (marketCap) => {
  if (marketCap == null) return '—';
  if (marketCap >= 100000) return `₹${(marketCap / 100000).toFixed(2)} Lakh Cr`;
  if (marketCap >= 1000) return `₹${(marketCap / 1000).toFixed(2)}K Cr`;
  return `₹${Number(marketCap).toFixed(2)} Cr`;
};
